import { FormFieldOption } from '../shared/form-field/form-field.component';
import { AddressType, CreditCardProcessorName, OneDotOrderStatus, PharmOrderStatus, PwpOrderStatus } from './models';

export const ORDER_STATUS_SEVERITIES = {
  [PwpOrderStatus.Authorize]: 'warning',
  [PwpOrderStatus.ReadyToShip]: 'success',
  [PwpOrderStatus.Shipped]: 'success',
  [PwpOrderStatus.Delivered]: 'success',
  [OneDotOrderStatus.ReadyToAuthorize]: 'warning',
  [OneDotOrderStatus.InsuranceInfoNeeded]: 'warning',
  [OneDotOrderStatus.DocumentationNeeded]: 'warning',
  [OneDotOrderStatus.ReadyToShip]: 'success',
  [OneDotOrderStatus.Shipped]: 'success',
  [OneDotOrderStatus.Delivered]: 'success',
  [OneDotOrderStatus.PaymentRequired]: 'warning',
  [PharmOrderStatus.NeedsConsent]: 'warning',
  [PharmOrderStatus.ReadyToAuthorize]: 'warning',
  [PharmOrderStatus.ReadyToShip]: 'success',
  [PharmOrderStatus.Shipped]: 'success',
};

export interface AddressTypeOption extends FormFieldOption {
  tagLabel: string;
  newAddressLabel: string;
  missingTypeErrorMessage: string;
  buttonLabel: string;
}

export const ADDRESS_TYPE_OPTIONS: AddressTypeOption[] = [
  {
    label: 'Set as default shipping address',
    value: 'shipping' as AddressType,
    tagLabel: 'Shipping',
    newAddressLabel: 'Make this my default shipping address',
    missingTypeErrorMessage: 'Please choose a default shipping address.',
    buttonLabel: 'Set Default Shipping',
  },
  {
    label: 'Set as default billing address',
    value: 'billing' as AddressType,
    tagLabel: 'Billing',
    newAddressLabel: 'Make this my default billing address',
    missingTypeErrorMessage: 'Please choose a default billing address.',
    buttonLabel: 'Set Default Billing',
  },
  {
    label: 'Set as default correspondence address',
    value: 'letter' as AddressType,
    tagLabel: 'Correspondence',
    newAddressLabel: 'Make this my default correspondence address',
    missingTypeErrorMessage: 'Please choose a default correspondence address.',
    buttonLabel: 'Set Default Correspondence',
  },
];

export const SHIPPING_ADDRESS_TYPE: AddressType = ADDRESS_TYPE_OPTIONS[0].value;
export const BILLING_ADDRESS_TYPE: AddressType = ADDRESS_TYPE_OPTIONS[1].value;
export const CORRESPONDENCE_ADDRESS_TYPE: AddressType = ADDRESS_TYPE_OPTIONS[2].value;

export interface CreditCardProcessor {
  key: string;
  value: CreditCardProcessorName;
  logoUrl: string;
  regexStrings: string[];
  cvvLength: number;
}

// Visa, Mastercard, American Express, Discover
export const CREDIT_CARD_PROCESSORS: CreditCardProcessor[] = [
  {
    key: 'visa',
    value: CreditCardProcessorName.Visa,
    logoUrl: './assets/images/visa-logo.svg',
    regexStrings: ['^4[0-9]{12}(?:[0-9]{3})?$'],
    cvvLength: 3,
  },
  {
    key: 'mastercard',
    value: CreditCardProcessorName.Mastercard,
    logoUrl: './assets/images/mastercard-logo.svg',
    regexStrings: ['^5[1-5][0-9]{14}$', '^2[2-7][0-9]{14}$'],
    cvvLength: 3,
  },
  {
    key: 'amex',
    value: CreditCardProcessorName.AmericanExpress,
    logoUrl: './assets/images/amex-logo.svg',
    regexStrings: ['^3[47][0-9]{13}$'],
    cvvLength: 4,
  },
  {
    key: 'discover',
    value: CreditCardProcessorName.Discover,
    logoUrl: './assets/images/discover-logo.svg',
    regexStrings: ['^6011[0-9]{12}[0-9]*$', '^62[24568][0-9]{13}[0-9]*$', '^6[45][0-9]{14}[0-9]*$'],
    cvvLength: 3,
  },
];

export const MEDICARE_INSURANCE_TYPE = 'Medicare';
export const MEDICARE_REPL_INSURANCE_TYPE = 'Medicare Replacement';

export const SETS_RESERVOIRS_HCPCS_GROUP = 'Sets and Reservoirs';
export const SENSORS_HCPCS_GROUP = 'Sensors';
export const TESTING_SUPPLIES_HCPCS_GROUP = 'Testing Supplies';

export const DIABETIC_SHIPMENT_TYPE_ID = 10;
export const OSTOMY_SHIPMENT_TYPE_ID = 30;
export const CGM_SHIPMENT_TYPE_ID = 60;
export const UROLOGICAL_SHIPMENT_TYPE_ID = 80;

export const OU_SHIPMENT_TYPE_IDS = [OSTOMY_SHIPMENT_TYPE_ID, UROLOGICAL_SHIPMENT_TYPE_ID];
export const REORDER_CONCLUSION_SHIPMENT_TYPE_IDS = [CGM_SHIPMENT_TYPE_ID, DIABETIC_SHIPMENT_TYPE_ID];

export const COLLECT_PAYMENT_THRESHOLD_USD = 0;
export const COLLECT_PAYMENT_OPTIONAL_THRESHOLD_USD = 100;

export const SHIPPING_SOON_THRESHOLD_DAYS = 3;

export const EXIT_ORDER_MESSAGES = {
  default: 'You cannot authorize your order at this time.',
  cannotConsentOrder:
    'It is too early to authorize this order. When you are down to 10 days of supplies or less, you can authorize your shipment. If you need to order today, please cancel and correct the amount of supplies you have left.',
  missingNextProviderVisit:
    'We apologize the order cannot be authorized at this time. Please contact your provider to schedule an appointment. Once scheduled your order will be available to consent.',
  invalidNextProviderVisit:
    'We apologize the order cannot be authorized at this time as we are unable to ship until your provider visit. If anything changes your order will be available to consent.',
};

export const FUTURE_VALID_PROVIDER_VISIT_THRESHOLD_DAYS = 14;

export const NOTES_COPY = {
  // canAutoComputeTargetShipDate
  dosHasMissingReorderItems: 'Dates of service data is missing one or more reorder items',
  hasUnknownOrInvalidDosData: 'Unknown or invalid dates of service data',
  hasUnknownShipOrConsentDates: 'One or more item shipment dates or authorize dates are unknown',
  hasDifferentItemShipDates: 'One or more items have different shipment dates',
  hasFutureItemConsentDates: 'Order contains an item that cannot be authorized at this time',
  hasUnknownDosEarliestOrderCanShip: 'Unknown earliest ship date for order',

  // doNotFetchCopay
  canAutoComputeTargetShipDate: 'Could not automatically compute order target ship date',
  hasUnknownOrInvalidReorderData: 'Unknown or invalid reorder data',
  isNonBillableShipmentType: 'Shipment type is not billable',
  isInsuranceChanged: 'Patient has requested an insurance change',
  skipCollectionForReconsent: 'Collection status indicates no payment is required at the time of reconsent',

  // hideSummaryStepBillingInfo
  doNotFetchCopay: 'Should not automatically compute copay',
  hasUnknownOrInvalidCopayData: 'Unknown or invalid copay data',

  // skipBillingStep
  hideSummaryStepBillingInfo: 'Could not compute copay',
  patientResponsiblityLessThanOrEqualToThreshold: `Patient estimated responsibility is less than or equal to \$${COLLECT_PAYMENT_THRESHOLD_USD}.00`,
  hasUnknownOrInvalidPaymentMethods: 'Unknown or invalid patient payment methods',

  // submitToAgent
  isAgentShipmentType: 'Order shipment type must be submitted to agent',
  haveTestingSuppliesQuantitiesChanged: 'Diabetic testing supplies quantities have been changed',
  hasUserRemovedAllItems: 'User set the quantity of all items to zero',
  hasQuantityValidationIssues: 'Order quantites could not be validated',
  hasUnknownOrInvalidDohRequirements: 'Unknown or invalid days on hand requirements',
  hasUnknownOrInvalidFrequenciesRequirements: 'Unknown or invalid frequencies requirements',
  hasUnknownOrInvalidPaymentMethodsWithResponsibility:
    'Unknown or invalid patient payment methods and payment is required',
  hasUnknownOrInvalidProviderVisitRequirements: 'Unknown or invalid provider visit requirements',
  hasUnknownOrInvalidProviderVisitValidation: 'Unknown or invalid provider visit validation result',
  hasPaymentFailed: 'Payment collection failed',
};

export const ERROR_PAGE_MESSAGE_CUSTOMIZATIONS = [
  {
    regexString: 'Patient does not have primary insurance', // From loading insurances
    errorTitle: 'Active Primary Insurance Not Found',
    errorMessage: 'No active primary insurance was found in our records. This order cannot be authorized at this time.',
  },
  {
    regexString: 'Patient has no active primary insurance', // PWO error from loading order wizard
    errorTitle: 'Active Primary Insurance Not Found',
    errorMessage: 'No active primary insurance was found in our records. This order cannot be authorized at this time.',
  },
  {
    regexString: 'Required Site Change Days is missing or zero',
    errorTitle: 'Something went wrong',
    errorMessage: 'This order cannot be authorized at this time.',
  },
  {
    regexString: 'the shipItem must be included and set to Y or N',
    errorTitle: 'Something went wrong',
    errorMessage: 'This order cannot be authorized at this time.',
  },
  {
    regexString: 'Reorder has invalid duplicate items',
    errorTitle: 'Something went wrong',
    errorMessage: 'This order cannot be authorized at this time.',
  },
  {
    regexString: 'There are no items in p_in_json_string',
    errorTitle: 'Something went wrong',
    errorMessage: 'This order cannot be authorized at this time.',
  },
  {
    regexString: 'Missing patient orders',
    errorTitle: 'Order Not Found',
    errorMessage: 'This order is not found.',
  },
  {
    regexString: 'This order is already processed. Please follow progress for shipment number',
    errorTitle: 'Order Already Processed',
    errorMessage: 'This order is already processed.',
  },
  {
    regexString: 'Order .* not found',
    errorTitle: 'Order Not Found',
    errorMessage: 'This order is not found.',
  },
  {
    regexString: 'This order is in process. Current status:',
    errorTitle: 'Order In Process',
    errorMessage: 'This order is currently being processed.',
  },
];

export const REGISTRATION_PAGE_MESSAGE_CUSTOMIZATIONS = [
  {
    regexString: 'Patient already migrated',
    errorMessage: 'User already exists for the provided email or patient ID.',
  },
  {
    regexString: 'Patient already registered',
    errorMessage: 'User already exists for the provided email or patient ID.',
  },
  {
    regexString: 'patient is already registered in user table',
    errorMessage: 'User already exists for the provided email or patient ID.',
  },
];
